import { DocumentAttributeWiringSection } from "~/components/domain/settings/documents/DocumentAttributeWiringSection";
import { DocumentDisplayUomSettingsFields } from "~/components/domain/settings/documents/DocumentDisplayUomSettingsFields";
import { DocumentSettingsEditor } from "~/components/domain/settings/documents/DocumentSettingsEditor";
import { OptInOrderDocumentAccessInfoAlert } from "~/components/domain/settings/documents/OptInOrderDocumentAccessInfoAlert";
import { BorderUsDocumentConfigSchema } from "~/modules/orders/order-documents/border-us/config.schema";
import { useOptInOrderDocumentSettingsForm } from "~/modules/orders/order-menu-document-extensions/useOptInOrderDocumentSettingsForm";
import { ORDER_DOCUMENT_SLUG, orderDocumentMetadataBySlug } from "~/shared/documents/order-document-settings";
import {
  BORDER_US_ATTRIBUTE_WIRE_CATALOG,
  mergeDocumentAttributeWiring,
} from "~/shared/documents/schemas/document-attribute-wiring.schema";
import { Divider, Input, Stack, Text } from "~/ui";
import { emptyEditorDoc } from "~/utils/types/rich-text";

const BORDER_US_SLUG = ORDER_DOCUMENT_SLUG.borderUs;
const BORDER_US_LABEL = orderDocumentMetadataBySlug[BORDER_US_SLUG].menuLabel;

const defaultEmptyConfig = () =>
  BorderUsDocumentConfigSchema.parse({
    header: emptyEditorDoc(),
    customsNotify: emptyEditorDoc(),
    declarationOfShipper: emptyEditorDoc(),
  });

/** Per-slug org settings editor for border-us order document boilerplate. @see docs/OPT_IN_ORDER_DOCUMENTS.md */
export const BorderUsOrderDocumentSettingsForm = () => {
  const { formElement } = useOptInOrderDocumentSettingsForm({
    slug: BORDER_US_SLUG,
    schema: BorderUsDocumentConfigSchema,
    emptyConfig: defaultEmptyConfig(),
    saveErrorTitle: "Error while saving Border U.S. document settings",
    saveSuccessTitle: "Border U.S. document settings saved",
    infoAlert: <OptInOrderDocumentAccessInfoAlert documentLabel={BORDER_US_LABEL} />,
    renderFields: (form, canEdit) => (
      <Stack spacing="md">
        <Input.Wrapper label="Header block">
          <DocumentSettingsEditor disabled={!canEdit} {...form.getInputProps("header")} />
        </Input.Wrapper>
        <Input.Wrapper label="For Customs Clearance Notify">
          <DocumentSettingsEditor disabled={!canEdit} {...form.getInputProps("customsNotify")} />
        </Input.Wrapper>
        <Input.Wrapper label="Declaration of Shipper">
          <DocumentSettingsEditor disabled={!canEdit} {...form.getInputProps("declarationOfShipper")} />
        </Input.Wrapper>
        <Text size="sm" color="dimmed">
          Built-in U.S. border documents always include a TSCA certificate with “Comply with TSCA” checked. A signature is required when
          creating the document.
        </Text>
        <Divider />
        <DocumentDisplayUomSettingsFields form={form} canEdit={canEdit} createModalLabel="Border U.S. create" />
        <Text size="sm" color="dimmed">
          Applies to gross weight. Qty/Packaging keeps the units entered on each line.
        </Text>
        <Divider />
        <DocumentAttributeWiringSection
          catalog={BORDER_US_ATTRIBUTE_WIRE_CATALOG}
          wiring={form.values.attributeWiring ?? {}}
          onWireChange={(wireKey, attributeIds) =>
            form.setFieldValue("attributeWiring", mergeDocumentAttributeWiring(form.values.attributeWiring, wireKey, attributeIds))
          }
          canEdit={canEdit}
        />
      </Stack>
    ),
  });

  return formElement;
};
